import { ALL_PERMISSIONS } from './permissions.js';

export const PANEL_PERMISSION_DEPENDENCIES = {
    'panel:dashboard': ['users:read'],
    'panel:pagos': ['agreements:update'],
    'panel:gestion_acelerador': ['agreements:update'],
    'panel:crear_acuerdo': ['agreements:create'],
    'panel:registro_financiero': ['finances:assign_gains'],
    'panel:inversiones': ['agreements:update'],
    'panel:usuarios': ['users:read'],
    'panel:perfil_admin': ['users:update'],
    'panel:retiros': ['withdrawals:process'],
    'panel:pagos_globales': ['finances:assign_gains'],
    'panel:pequenos_duenos': ['agreements:update'],
    'panel:reportes': ['users:read'],
    'panel:club_beneficios': ['platform_settings:update'],
    'panel:notificaciones': ['support:handle_tickets'],
    'panel:soporte': ['support:handle_tickets'],
    'panel:utilidades': ['maintenance:run_tasks'],
    'panel:auditoria': ['maintenance:run_tasks'],
    'panel:configuracion': ['platform_settings:update'],
};

export const FUNCTION_PERMISSION_DEPENDENTS = Object.entries(PANEL_PERMISSION_DEPENDENCIES).reduce((acc, [panelPermission, requiredPermissions]) => {
    requiredPermissions.forEach((dependency) => {
        if (!acc[dependency]) {
            acc[dependency] = [];
        }
        acc[dependency].push(panelPermission);
    });
    return acc;
}, {});

export const PERMISSION_INFO = {
    'panel:dashboard': {
        label: 'Ver Dashboard',
        description: 'Accede al resumen general del panel administrativo.',
    },
    'panel:pagos': {
        label: 'Ver Solicitudes de Aporte',
        description: 'Muestra la sección para revisar aportes pendientes.',
    },
    'panel:gestion_acelerador': {
        label: 'Ver Acelerador',
        description: 'Permite ingresar a la sección de solicitudes del acelerador.',
    },
    'panel:crear_acuerdo': {
        label: 'Ver Crear Acuerdo',
        description: 'Habilita el acceso a la pantalla de creación manual de acuerdos.',
    },
    'panel:registro_financiero': {
        label: 'Ver Registro Financiero',
        description: 'Accede al historial de movimientos consolidado.',
    },
    'panel:inversiones': {
        label: 'Ver Participaciones',
        description: 'Permite revisar el listado de participaciones activas.',
    },
    'panel:usuarios': {
        label: 'Ver Usuarios',
        description: 'Da acceso a la administración de usuarios y sus roles.',
    },
    'panel:perfil_admin': {
        label: 'Ver Perfil Administrativo',
        description: 'Permite acceder a la pantalla de edición de perfil admin.',
    },
    'panel:retiros': {
        label: 'Ver Retiros',
        description: 'Muestra la bandeja de solicitudes de retiro.',
    },
    'panel:pagos_globales': {
        label: 'Ver Pagos Globales',
        description: 'Habilita la sección para ejecutar pagos globales.',
    },
    'panel:pequenos_duenos': {
        label: 'Ver Pequeños Dueños',
        description: 'Permite entrar al módulo de pequeños dueños.',
    },
    'panel:reportes': {
        label: 'Ver Reportes',
        description: 'Da acceso a la sección de reportes administrativos.',
    },
    'panel:club_beneficios': {
        label: 'Club de Beneficios',
        description: 'Permite gestionar los enlaces y ofertas del Club de Beneficios.',
    },
    'panel:notificaciones': {
        label: 'Ver Notificaciones',
        description: 'Permite gestionar las notificaciones administrativas.',
    },
    'panel:soporte': {
        label: 'Ver Soporte',
        description: 'Habilita el acceso al buzón de soporte.',
    },
    'panel:auditoria': {
        label: 'Ver Auditoría',
        description: 'Permite revisar el historial de auditoría del sistema.',
    },
    'panel:utilidades': {
        label: 'Ver Utilidades',
        description: 'Accede a las herramientas avanzadas y rutinas de mantenimiento.',
    },
    'panel:configuracion': {
        label: 'Ver Configuración',
        description: 'Da acceso al módulo de configuración del panel.',
    },
    'users:read': {
        label: 'Ver usuarios',
        description: 'Permite consultar la lista de usuarios y sus datos básicos.',
    },
    'users:update': {
        label: 'Editar usuarios',
        description: 'Autoriza actualizar perfiles (nombre, billeteras, etc.).',
    },
    'users:update_role': {
        label: 'Cambiar roles',
        description: 'Permite asignar roles administrativos a otros usuarios.',
    },
    'users:delete': {
        label: 'Desactivar usuarios',
        description: 'Autoriza desactivar cuentas y reasignar referidos.',
    },
    'agreements:create': {
        label: 'Crear acuerdos manuales',
        description: 'Habilita la creación de acuerdos desde el panel de administración.',
    },
    'agreements:cancel': {
        label: 'Cancelar acuerdos',
        description: 'Permite cerrar acuerdos activos o pendientes por motivos administrativos.',
    },
    'agreements:update': {
        label: 'Actualizar acuerdos',
        description: 'Autoriza modificar información clave de acuerdos existentes (estado, fechas, notas).',
    },
    'withdrawals:process': {
        label: 'Gestionar retiros',
        description: 'Autoriza aprobar o rechazar solicitudes de retiro.',
    },
    'finances:assign_gains': {
        label: 'Asignar resultados',
        description: 'Acceso para ejecutar pagos globales o ingresos manuales.',
    },
    'finances:revert_gains': {
        label: 'Revertir resultados',
        description: 'Permite revertir pagos de ROI realizados por error.',
    },
    'finances:adjust_balance': {
        label: 'Ajustar saldos',
        description: 'Autoriza sumar o restar saldo disponible para retiro a cuentas de usuarios.',
    },
    'platform_settings:update': {
        label: 'Configurar plataforma',
        description: 'Permite cambiar parámetros globales como tasas y banderas de retiro.',
    },
    'support:handle_tickets': {
        label: 'Atender soporte',
        description: 'Da acceso al buzón de tickets y a responder a los usuarios.',
    },
    'maintenance:run_tasks': {
        label: 'Ejecutar utilidades',
        description: 'Permite lanzar tareas de mantenimiento y scripts de respaldo.',
    },
};

export const PERMISSION_GROUPS = [
    {
        key: 'panel',
        title: 'Acceso a módulos del panel',
        permissions: [
            'panel:dashboard',
            'panel:pagos',
            'panel:gestion_acelerador',
            'panel:crear_acuerdo',
            'panel:registro_financiero',
            'panel:inversiones',
            'panel:usuarios',
            'panel:perfil_admin',
            'panel:retiros',
            'panel:pagos_globales',
            'panel:pequenos_duenos',
            'panel:reportes',
            'panel:club_beneficios',
            'panel:notificaciones',
            'panel:soporte',
            'panel:utilidades',
            'panel:auditoria',
            'panel:configuracion',
        ],
    },
    {
        key: 'usuarios',
        title: 'Usuarios y roles',
        permissions: ['users:read', 'users:update', 'users:update_role', 'users:delete'],
    },
    {
        key: 'acuerdos',
        title: 'Acuerdos y soporte',
        permissions: ['agreements:create', 'agreements:cancel', 'agreements:update', 'support:handle_tickets'],
    },
    {
        key: 'finanzas',
        title: 'Finanzas',
        permissions: ['withdrawals:process', 'finances:assign_gains', 'finances:revert_gains', 'finances:adjust_balance'],
    },
    {
        key: 'sistema',
        title: 'Configuración y mantenimiento',
        permissions: ['platform_settings:update', 'maintenance:run_tasks'],
    },
];

export const ROLE_DESCRIPTIONS = {
    superadmin: {
        label: 'Super Admin',
        summary: 'Acceso irrestricto al panel. No se ve afectado por los permisos configurados para otros roles.',
    },
    admin: {
        label: 'Administrador',
        summary: 'Acceso completo al panel para aprobar aportes, retiros y configurar la plataforma.',
    },
    support: {
        label: 'Soporte',
        summary: 'Perfil enfocado en resolver tickets y revisar reportes sin acceso financiero.',
    },
    user: {
        label: 'Usuario',
        summary: 'Sin acceso al panel administrativo; usa únicamente la app de participante.',
    },
};

const buildPreset = (enabledPermissions = []) => {
    const enabledSet = new Set(enabledPermissions);
    return ALL_PERMISSIONS.reduce((acc, permission) => {
        acc[permission] = enabledSet.has(permission);
        return acc;
    }, {});
};

const ADMIN_BASE_EXCLUSIONS = new Set([
    'panel:configuracion',
    'panel:auditoria',
    'panel:pagos',
]);

const ADMIN_DEFAULT_PERMISSIONS = ALL_PERMISSIONS.filter(
    (permission) => !ADMIN_BASE_EXCLUSIONS.has(permission)
);

export const ROLE_PERMISSION_PRESETS = {
    admin: buildPreset(ADMIN_DEFAULT_PERMISSIONS),
    support: buildPreset([
        'support:handle_tickets',
        'panel:soporte',
        'panel:notificaciones',
        'panel:reportes',
    ]),
    user: buildPreset([]),
};
